import React from 'react';
import axios from 'axios';

import { PREFIX_API, PREFIX_FILES_API } from '../Configuration';

const WebclientContext = React.createContext();

export const WebclientURL = {
    login: PREFIX_API + '/user/login',
    signup: PREFIX_API + '/user/signup',
    profile: PREFIX_API + '/user/profile',
    profile_update: PREFIX_API + '/user/profile',
    password_reset_request: PREFIX_API + '/user/password-reset/request',
    password_reset_confirm: PREFIX_API + '/user/password-reset/confirm',
    purchases: PREFIX_API + '/user/purchases',
    purchase_create: PREFIX_API + '/user/purchases',
    purchase_capture: PREFIX_API + '/user/purchases/(purchase_id)/capture',
    purchase_detail: PREFIX_API + '/user/purchases/(purchase_id)',
    purchase_by_token: PREFIX_API + '/user/purchases/by-token/(token)',

    admin_login: PREFIX_API + '/mouseadmin/login',

    articles_all: PREFIX_API + '/articles',
    articles: PREFIX_API + '/articles/tags',
    article: PREFIX_API + '/article/(title_slug)',
    article_by_id: PREFIX_API + '/article/id/(id)',
    article_update: PREFIX_API + '/article/(id)',
    article_create: PREFIX_API + '/article',
    article_upload_file: PREFIX_API + '/article/(id)/files',
    article_delete_file: PREFIX_API + '/article/(id)/files/delete',

    tags_all: PREFIX_API + '/tags',
    tags: PREFIX_API + '/tags?sections=(sections)&lang=ru',
    tag_create: PREFIX_API + '/tags',
    tag_update: PREFIX_API + '/tag/(id)',

    article_file: PREFIX_FILES_API + '/attachments/(title_slug)/(filename)',
};

const AUTHORIZATION_HEADER = 'Authorization';

// The API answers an expired or invalid token with these details, never a login failure.
const AUTH_ERROR_DETAILS = ['Invalid credentials', 'Invalid authorization type'];

const hasAuthErrorDetail = (data) => {
    const detail = data && data.detail;
    if (!detail) return false;
    const details = Array.isArray(detail) ? detail : [detail];
    return details.some((item) => AUTH_ERROR_DETAILS.includes(item));
};

export default function WebclientProvider({ children }) {
    const tokenRef = React.useRef(null);
    const sessionExpiredRef = React.useRef(null);

    // Memoized: a fresh axios instance would drop the Authorization header on every re-render.
    const webclient = React.useMemo(() => {
        const instance = axios.create({
            baseURL: PREFIX_API,
            timeout: 5000,
            validateStatus: (status) => {
                if ((status >= 200) && (status < 300)) return true;
                if ((status >= 400) && (status < 500)) return true;
                return false;
            },
        });

        // A rejected token must ask for a new login once, not surface as a raw
        // "Invalid credentials" message on every screen that happens to call the API.
        instance.interceptors.response.use((response) => {
            const rejectedToken = !!tokenRef.current && (
                response.status === 401 ||
                (response.status === 403 && hasAuthErrorDetail(response.data))
            );
            if (rejectedToken) {
                response.sessionExpired = true;
                if (sessionExpiredRef.current) sessionExpiredRef.current();
            }
            return response;
        });

        return instance;
    }, []);

    const doPost = ({ url, data, timeout }) => {
        return webclient.post(url, data, { timeout: timeout || 5000 });
    };

    const doGet = ({ url, data }) => {
        const newUrl = !!data ? url + '?' + new URLSearchParams(data).toString() : url;
        return webclient.get(newUrl);
    };

    // Downloads through axios so the Bearer token is sent — paid attachments 401/403 without it.
    const doGetFile = ({ url, timeout }) => {
        return webclient.get(url, { responseType: 'blob', timeout: timeout || 60000 });
    };

    const setAuthorization = (token) => {
        const headers = webclient.defaults.headers;
        tokenRef.current = token || null;
        if (token) {
            headers.common[AUTHORIZATION_HEADER] = 'Bearer ' + token;
        } else {
            headers.common = Object.fromEntries(
                Object.entries(headers.common).filter((item) => item[0] !== AUTHORIZATION_HEADER)
            );
        }
    };

    const setSessionExpiredHandler = (handler) => {
        sessionExpiredRef.current = handler || null;
    };

    const values = {
    };

    const funcs = {
        post: doPost,
        get: doGet,
        getFile: doGetFile,
        setAuthorization: setAuthorization,
        setSessionExpiredHandler: setSessionExpiredHandler,
    };

    return (
        <WebclientContext.Provider value={{ ...values, ...funcs }}>
            {children}
        </WebclientContext.Provider>
    )
}

export const useWebclient = () => React.useContext(WebclientContext);
